import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    expandEventsForMonth,
    groupAgendaByDay,
} from '../utils/calendarAgenda';
import { selectionFromAgendaItem } from '../utils/calendarEventSelection';

/**
 * @param {Date} monthCursor
 */
function startOfMonth(monthCursor) {
    return new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1, 12, 0, 0, 0);
}

/**
 * @param {import('../utils/calendarAgenda').AgendaInstance} item
 * @param {Intl.DateTimeFormat} timeFmt
 * @param {string} allDayLabel
 */
function formatEventTime(item, timeFmt, allDayLabel) {
    if (item.allDay) return allDayLabel;
    const start = timeFmt.format(new Date(item.startMs));
    const end = timeFmt.format(new Date(item.endMs));
    if (start === end) return start;
    return `${start} – ${end}`;
}

/**
 * Mobil etkinlik takvimi — ay görünümü yerine günlük gruplu liste.
 * @param {{
 *   events: Record<string, unknown>[],
 *   onEventSelect?: (selection: import('../utils/calendarEventSelection').CalendarEventSelection) => void,
 * }} props
 */
export function EventCalendarMobileAgenda({ events, onEventSelect }) {
    const { t, i18n } = useTranslation();
    const [monthCursor, setMonthCursor] = useState(() => startOfMonth(new Date()));

    const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-GB' : 'tr-TR';

    const monthTitleFmt = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                month: 'long',
                year: 'numeric',
            }),
        [locale],
    );

    const dayHeadingFmt = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
            }),
        [locale],
    );

    const timeFmt = useMemo(
        () =>
            new Intl.DateTimeFormat(locale, {
                hour: '2-digit',
                minute: '2-digit',
            }),
        [locale],
    );

    const allDayLabel = t('eventCalendarPage.agendaAllDay');

    const dayGroups = useMemo(() => {
        const instances = expandEventsForMonth(
            events,
            monthCursor.getFullYear(),
            monthCursor.getMonth(),
        );
        return groupAgendaByDay(
            instances.map((item) => ({
                ...item,
                timeLabel: formatEventTime(item, timeFmt, allDayLabel),
            })),
        );
    }, [events, monthCursor, timeFmt, allDayLabel]);

    const goMonth = (delta) => {
        setMonthCursor((prev) => {
            const next = new Date(prev);
            next.setMonth(next.getMonth() + delta, 1);
            return startOfMonth(next);
        });
    };

    const goToday = () => setMonthCursor(startOfMonth(new Date()));

    const isCurrentMonth =
        monthCursor.getFullYear() === new Date().getFullYear() &&
        monthCursor.getMonth() === new Date().getMonth();

    return (
        <div className="event-calendar-agenda">
            <div
                className="event-calendar-agenda-toolbar"
                role="toolbar"
                aria-label={t('eventCalendarPage.agendaToolbarAria')}
            >
                <div className="event-calendar-agenda-toolbar-nav">
                    <button
                        type="button"
                        className="event-calendar-agenda-btn"
                        onClick={() => goMonth(-1)}
                        aria-label={t('eventCalendarPage.agendaPrevMonth')}
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className="event-calendar-agenda-btn"
                        onClick={() => goMonth(1)}
                        aria-label={t('eventCalendarPage.agendaNextMonth')}
                    >
                        ›
                    </button>
                </div>
                <h2 className="event-calendar-agenda-month-title">
                    {monthTitleFmt.format(monthCursor)}
                </h2>
                <button
                    type="button"
                    className={`event-calendar-agenda-today${isCurrentMonth ? ' is-disabled' : ''}`}
                    onClick={goToday}
                    disabled={isCurrentMonth}
                >
                    {t('eventCalendarPage.agendaToday')}
                </button>
            </div>

            {dayGroups.length === 0 ? (
                <p className="event-calendar-agenda-empty text-secondary mb-0">
                    {t('eventCalendarPage.agendaEmptyMonth')}
                </p>
            ) : (
                <ul className="event-calendar-agenda-days list-unstyled mb-0">
                    {dayGroups.map((group) => {
                        const isToday =
                            ymdToday() === group.date;
                        return (
                            <li
                                key={group.date}
                                className={`event-calendar-agenda-day${isToday ? ' event-calendar-agenda-day--today' : ''}`}
                            >
                                <div className="event-calendar-agenda-day-head">
                                    <time
                                        dateTime={group.date}
                                        className="event-calendar-agenda-day-label"
                                    >
                                        {dayHeadingFmt.format(group.dateObj)}
                                    </time>
                                </div>
                                <ul className="event-calendar-agenda-events list-unstyled mb-0">
                                    {group.events.map((item) => (
                                        <li key={item.id}>
                                            <button
                                                type="button"
                                                className={`event-calendar-agenda-item event-calendar-agenda-item--${item.kind}${onEventSelect ? ' event-calendar-agenda-item--clickable' : ''}`}
                                                onClick={
                                                    onEventSelect
                                                        ? () =>
                                                              onEventSelect(
                                                                  selectionFromAgendaItem(
                                                                      item,
                                                                  ),
                                                              )
                                                        : undefined
                                                }
                                            >
                                                <div className="event-calendar-agenda-item-body min-w-0">
                                                    <p className="event-calendar-agenda-item-time mb-1">
                                                        <span
                                                            className="event-calendar-agenda-item-dot"
                                                            aria-hidden
                                                        />
                                                        {item.timeLabel}
                                                    </p>
                                                    <p className="event-calendar-agenda-item-title mb-0">
                                                        {item.title}
                                                    </p>
                                                </div>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function ymdToday() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
